import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'data-root', label: 'Data root' },
  { id: 'cache', label: 'Analysis cache' },
  { id: 'models', label: 'Models' },
  { id: 'exports', label: 'Exports' },
  { id: 'logs', label: 'Logs' },
  { id: 'settings-license', label: 'Settings & license' },
  { id: 'moving-cache', label: 'Moving the cache folder' },
];

export default function Body() {
  return (
    <>
      <DocSection id="data-root" title="Data root">
        <p>
          Everything ClipRadar keeps for itself — models, your analysis cache, settings, your
          license — lives under one folder: <code>%APPDATA%\ClipRadar\data</code>. Your original
          media files never move or get copied there; ClipRadar only ever reads them from wherever
          you already keep them.
        </p>
        <DocShot
          slug="where-your-data-lives"
          shot="storage"
          alt="Settings → Storage, showing the current cache folder path with Browse and Reset to default"
          caption="Settings → Storage."
          callouts={[
            { n: 1, label: 'Current cache folder path' },
            { n: 2, label: 'Browse — choose a new location' },
            { n: 3, label: 'Reset to default' },
          ]}
        />
      </DocSection>

      <DocSection id="cache" title="Analysis cache">
        <p>
          Every source you add — every file, or every item inside a watched folder — gets its own
          slice of the cache: thumbnails, visual embeddings, transcripts, and face data, all keyed
          to that one file. The cache only ever stores <em>derived</em> data, never a copy of your
          original media — delete a source from <span style={kbd}>Media</span> and its cache is
          cleaned up with it.
        </p>
      </DocSection>

      <DocSection id="models" title="Models">
        <p>
          Downloaded AI models live in a <code>models</code> folder inside the data root. See{' '}
          <Link to="/docs/reference/models">Machine-learning models</Link> for what's in there,
          what each one does, and roughly how big it gets.
        </p>
      </DocSection>

      <DocSection id="exports" title="Exports">
        <p>
          Exported clips, timeline XML files, and transcript exports go to a separate{' '}
          <strong>Export folder</strong> — by default <code>Videos\FootageSearch\Exports</code> in
          your user folder, changeable in <span style={kbd}>Settings → Export</span>. It's the one
          place ClipRadar writes copies of your footage; the Cache folder never does.
        </p>
      </DocSection>

      <DocSection id="logs" title="Logs">
        <p>
          ClipRadar writes two log files as it runs — a backend log and an app log — both under{' '}
          <code>%APPDATA%\ClipRadar\logs</code>. Log lines are scrubbed of personal information
          (usernames, file paths) as they're written, then rotated so they don't grow forever.
        </p>
        <DocShot
          slug="where-your-data-lives"
          shot="reveal-logs"
          alt="The ClipRadar logs folder open in File Explorer"
          caption="Paste %APPDATA%\ClipRadar\logs into File Explorer's address bar to open the logs folder directly."
        />
        <p>
          You'll rarely need to open this folder yourself — when you check{' '}
          <strong>Include diagnostics</strong> while reporting a bug from Help, ClipRadar attaches
          the recent log lines automatically, scrubbed a second time before anything leaves your
          machine.
        </p>
      </DocSection>

      <DocSection id="settings-license" title="Settings & license">
        <p>
          Your settings live in <code>settings.json</code> and your license state in{' '}
          <code>license.json</code>, both directly in the data root. Settings and Licensing in the
          app read and write these for you — there's no reason to edit them by hand. See{' '}
          <Link to="/docs/concepts/licensing">Licensing</Link> for how the license file is used.
        </p>
      </DocSection>

      <DocSection id="moving-cache" title="Moving the cache folder">
        <p>
          If your system drive is small, move the cache somewhere bigger:{' '}
          <span style={kbd}>Settings → Storage → Browse</span>, then pick a new folder. If that
          folder already has ClipRadar cache data in it, you'll be asked whether to{' '}
          <strong>move</strong> what's already there or <strong>leave</strong> it and start fresh
          at the new location. <strong>Reset to default</strong> puts the cache folder back under{' '}
          <code>%APPDATA%\ClipRadar\data</code>.
        </p>
      </DocSection>
    </>
  );
}
